import { isoDayOf, isoLettingDayOf } from '@edgevanta/bid-tab-extract/iso-day';
import { ConflictError } from '@edgevanta/http-errors';

import {
  type ConsolidatedMatrix,
  type ConsolidatedMatrixItem,
  type InvalidBidStatus,
  matrixBidders,
} from './persist';

/**
 * The custom proposal's `staging` matrix — the published/working copy.
 *
 * It is a STRUCTURAL SUPERSET of {@link ConsolidatedMatrix}: the same
 * `bidGroups → schedules → items` tree with the per-item `bids` keyed by the
 * column's `vendorColumnId` (never the name), PLUS an explicit read-vs-link
 * separation on every node:
 *  - READ fields = exactly what the file printed (never overwritten).
 *  - LINK fields = the resolved connection to a real platform record. They
 *    start `null`; a future normalization pass (delegated) fills them. This pass
 *    only CARRIES them so the review UI can read/write per tab.
 *
 * The column axis is `vendors[]` ({@link StagingVendor}); a bids/total key is a
 * `vendorColumnId` resolved to a printed name through it. A vendor IS its column,
 * so renaming the label never re-keys a cell.
 */

/** One collection a line belongs to, WITH its resolved conversion factor. The
 *  factor is decided at normalize (from the classify assignment via
 *  `resolveCollectionFactor`), carried on the line, and consumed at publish. */
export interface LineCollection {
  /** Collection code (e.g. 'asphalt_tons'). */
  code: string;
  /** Resolved conversion factor from the line's unit to the collection's. */
  factor: number;
  /** The line's source unit-of-measure the factor converts FROM. */
  sourceUom: string;
}

/** A vendor COLUMN (the matrix's column axis). An entity: a stable id, an
 *  editable label (the printed name), and the resolved DB refs. READ = label;
 *  LINK = refs, null until resolved. */
export interface StagingVendor {
  /** STABLE synthetic COLUMN identity (`v0`, `v1`, …) — minted at `assemble`,
   *  carried verbatim into staging, opaque, NEVER derived from the name. The key
   *  every `bids` / `total` / `totals` map uses, and the durable handle the
   *  normalization store + the draft round-trip key the column on, so RENAMING the
   *  vendor in the Vendors tab can't orphan its cells or its resolved link. The
   *  row-axis twin of `StagingItem.itemLineId`.
   *
   *  One exception, for legacy staging that predates the field and keys its
   *  cells by the printed name instead: a column with NO id is given one on read
   *  (`withStagingIds`), from that name, so the cells it already has still
   *  answer to it. What the rule protects is unaffected, since the id is frozen
   *  once minted and a rename never touches it. */
  vendorColumnId: string;
  /** Vendor name exactly as printed on the sheet — the editable label. Never a key. */
  name: string;
  /** → global_vendors.id. Null until resolved. */
  globalVendorId: string | null;
  /** → this owner's vendors.id. Null until resolved. */
  vendorId: string | null;
  /** The bid's AWARD STATUS when INVALID — withdrawn / rejected / non_responsive /
   *  irregular / no_bid (EDG-1588). Absent = a normal ('valid') bid. An invalid
   *  bid keeps its full price column but is excluded from the ranking, its totals
   *  are not graded as problems, and the UI badges it with the printed reason.
   *  Human-settable in the wizard. */
  status?: InvalidBidStatus;
  /** The printed reason the bid is not valid, verbatim ("Non-Responsive", "Late").
   *  Only when `status` set. */
  statusReason?: string | null;
}

/** One line item (the matrix's ROW). READ = printed fields; LINK = null until
 *  resolved. */
export interface StagingItem {
  /** STABLE synthetic ROW identity (the matrix's item axis), carried from the
   *  consolidated (minted at assemble). The row's durable handle — the wizard
   *  draft and the fold that writes edits back key on THIS, never on printed
   *  content, so editing any field (line/item number, description, qty, price)
   *  can't orphan the row. Its column-axis twin is `StagingVendor.vendorColumnId`.
   *  Optional only for legacy staging seeded before line ids existed. */
  itemLineId?: string;
  /** Line number as printed. Falls back to render position when absent. */
  lineNumber?: number | null;
  /** Item number as printed. */
  itemNumber?: string | number | null;
  /** Description as printed. */
  description?: string | null;
  /** Quantity as printed. */
  quantity?: number | null;
  /** Unit of measure as printed. */
  unit?: string | null;
  /** → items.id (catalog). Null until resolved. */
  itemId: string | null;
  /** What the hunt decided: this line reuses an existing owner item, or is new
   *  (created on publish). Null until the hunt runs. */
  itemMatch?: 'existing' | 'new' | null;
  /** → item_categories.id. Null until resolved. */
  categoryId: string | null;
  /** Category catalog code ('06'), for display without a re-lookup. */
  categoryCode?: string | null;
  /** → item_collections.id. Null until resolved. Superseded by
   *  {@link collectionCodes}; kept for back-compat (holds the first code). */
  collectionId: string | null;
  /** Every collection code this line belongs to. Derived mirror of
   *  {@link collections}[].code; kept for readers that only need the codes. */
  collectionCodes?: string[];
  /** Each collection membership WITH its resolved conversion factor — the paid
   *  normalization for this line's collections, so publish uses the stored
   *  factor instead of re-deriving 1.0, and the wizard can edit it on the line. */
  collections?: LineCollection[];
  /** → units.id (canonical). Null until resolved. */
  unitId?: string | null;
  /** Lump-sum flag from the matched attribute bundle. */
  isTrueLumpSum?: boolean;
  /** Liquid AC adjustment pattern (P1–P4) classified from the description, or
   *  null when the item needs none. */
  liquidAcPattern?: string | null;
  /** Per-vendor cells, keyed by the column's `vendorColumnId` (never the name). */
  bids?: Record<
    string,
    { unitPrice?: number | null; extendedPrice?: number | null } | null
  >;
}

/** A schedule within a group. `name` is read from the file AND user-settable. */
export interface StagingSchedule {
  /** Schedule name — read from file; the user may rename it. */
  name?: string | null;
  items?: StagingItem[];
  /** The schedule's printed total keyed by `vendorColumnId` — read off the page,
   *  never derived from the items. The reconcile oracle for the schedule rung
   *  (Σext == schedule) the price sheet grades each subtotal against. */
  total?: Record<string, number> | null;
}

/** A bid group. `name` is read from the file AND user-settable. */
export interface StagingBidGroup {
  type?: string | null;
  /** Group name — read from file; the user may rename it. */
  name?: string | null;
  /** EDG-1583: whether this ALTERNATE is AWARDED — the reviewer's accept
   *  decision on the Price sheet's award toggle. Meaningless on the base bid
   *  (always in the total); on an alternate it drives the comparison/adjusted
   *  total (awarded additive add, awarded deductive subtract) and the awarded
   *  badge in the results view. Absent/false = not awarded. */
  awarded?: boolean;
  schedules?: StagingSchedule[];
  /** Legacy/single-schedule fallback. */
  items?: StagingItem[];
  /** The bid group's printed total keyed by `vendorColumnId` — the reconcile
   *  oracle for the group rung (Σschedules == group). */
  totals?: Record<string, number> | null;
}

/** The custom proposal's header bag — the ONE definition of the identity /
 *  contract / estimating fields that live on `staging.metadata`.
 *
 *  This is the firm contract: every layer (domain, the API write schema, the
 *  client hook, the Details form) references THIS type, so renaming or adding a
 *  field surfaces at every consumer's type-check instead of drifting silently.
 *
 *  The named fields are what the CLASSIFY header emits + what normalize/save
 *  stamp on top. The index signature keeps it TOLERANT: the engine may carry
 *  extra CLASSIFY keys through verbatim, and a new one must not break a write —
 *  it rides here untyped until it earns a name. */
export interface StagingMetadata {
  /** Resolved owner (normalize/save stamps it); `ownerName` is the printed one. */
  ownerId?: string | null;
  ownerName?: string | null;
  /** Resolved state; `stateAbbreviation` is what the page printed. */
  stateId?: string | null;
  stateAbbreviation?: string | null;
  contractId?: string | null;
  callNumber?: string | null;
  // The two GLOBAL classification axes, stored as dim ids and pushed onto
  // `proposals` at publish. They replaced a free-text `contractType` that
  // never left this metadata.
  contractTypeId?: string | null;
  projectTypeId?: string | null;
  projectNumber?: string | null;
  route?: string | null;
  /** What the work IS: the printed project/work title, the scope prose, or both.
   *  Named for the either-or the whole way through — CLASSIFY captures it under
   *  this name and staging keeps it, so nothing has to translate mid-pipeline.
   *  The ONE place it becomes `description` is the write to `proposals`, which
   *  has a description column (see the publish payload in `use-wizard-actions`). */
  projectNameOrDescription?: string | null;
  /** yyyy-mm-dd. */
  lettingDate?: string | null;
  /** Exact printed form; Details writes null when the letting date is cleared. */
  lettingDatePrinted?: string | null;
  /** yyyy-mm-dd. */
  completionDate?: string | null;
  completionCalendarDays?: number | null;
  completionWorkingDays?: number | null;
  costEstimate?: number | string | null;
  /** Estimating (proposal_extensions) — percents ride as numeric strings. */
  markupPercent?: number | string | null;
  overheadPercent?: number | string | null;
  marginPercent?: number | string | null;
  businessUnitId?: string | null;
  workTypeIds?: string[];
  estimatorIds?: string[];
  districtIds?: string[];
  countyIds?: string[];
  /** Extra CLASSIFY keys the engine carries through — untyped, never rejected. */
  [key: string]: unknown;
}

/** The custom proposal's published/working matrix — superset of ConsolidatedMatrix. */
export interface CustomProposalStaging {
  /** The vendor set with read + link fields. */
  vendors: StagingVendor[];
  /** Kept for back-compat with readers that expect a bare name list. */
  bidders: string[];
  bidGroups: StagingBidGroup[];
  /** The `vendorColumnId`s that are the ENGINEER'S / owner's estimate — a
   *  reference column, NOT a competing bid. Publish reads this to divert the
   *  estimate to the proposal's cost-estimate instead of materializing it as a
   *  vendor. MUST be carried through from the consolidated or the estimate ships
   *  as a (winning) vendor. */
  estimates?: string[];
  metadata?: StagingMetadata | null;
}

/** The top-level keys a page can own and write. Each is versioned on its own —
 *  see {@link stagingFingerprint}. */
export type StagingSliceKey = keyof CustomProposalStaging;

const RESET_REVISION_KEY = '__resetRevision' as const;
type ResetVersionedStaging = CustomProposalStaging & {
  [RESET_REVISION_KEY]?: string;
};

export function withResetRevision(
  staging: CustomProposalStaging,
  revision: string,
): CustomProposalStaging {
  return {
    ...staging,
    [RESET_REVISION_KEY]: revision,
  } as ResetVersionedStaging;
}

export function withoutResetRevision(
  staging: CustomProposalStaging,
): CustomProposalStaging {
  const clean = { ...staging } as ResetVersionedStaging;
  delete clean[RESET_REVISION_KEY];
  return clean;
}

export function resetRevisionOf(
  staging: CustomProposalStaging | null | undefined,
): string | null {
  const revision = (staging as ResetVersionedStaging | null | undefined)?.[
    RESET_REVISION_KEY
  ];
  return typeof revision === 'string' ? revision : null;
}

/** The header a staging save carries its concurrency token on.
 *
 *  NOT `If-Match`, and never a standard conditional header. `If-Match` is
 *  reserved by RFC 9110 §13.1.1 for ETag preconditions, and the CDN in front of
 *  the API answers it itself: a value that is not the resource's ETag comes back
 *  as `412 Precondition Failed` without the request ever reaching the route. A
 *  token that means something only to this application belongs on a header no
 *  intermediary is permitted to act on.
 *
 *  Defined once, here, so the client and the route cannot drift apart. */
export const STAGING_FINGERPRINT_HEADER = 'x-staging-fingerprint';

/** Key-order-independent JSON — the stored matrix is jsonb, so Postgres decides
 *  the key order of what comes back. Hashing the raw serialization would make a
 *  round-trip through the DB look like an edit. */
function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value ?? null) ?? 'null';
  }
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(',')}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries
    .map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`)
    .join(',')}}`;
}

/** FNV-1a. Not cryptographic on purpose: this only has to notice that a slice
 *  changed, and it has to produce the SAME digest in the browser and on the
 *  server, so it cannot reach for node:crypto. */
function hash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

/** The concurrency token for a set of staging slices — a digest of exactly the
 *  keys a page owns, so a write to any OTHER key is invisible to it.
 *
 *  This is deliberately NOT the row's `updatedAt`: normalization alone bumps
 *  that 5–7 times (status → normalized → staging → status), items-resolve bumps
 *  it on the Items tab, and publish bumps it again. Against a row timestamp,
 *  every one of those reads as "someone edited your form" and a Details save
 *  that touches only `metadata` gets rejected for a change it does not conflict
 *  with. Against this, only a write to `metadata` itself can. */
export function stagingFingerprint(
  staging: CustomProposalStaging | null | undefined,
  keys: readonly StagingSliceKey[],
  resetRevision = resetRevisionOf(staging),
): string {
  return [...keys]
    .sort()
    .map((key) => {
      const sliceDigest = hash(canonicalJson(staging?.[key] ?? null));
      const digest = resetRevision
        ? `${resetRevision}.${sliceDigest}`
        : sliceDigest;
      return `${key}:${digest}`;
    })
    .join('|');
}

/** The per-slice digests a fingerprint carries. Parsed rather than compared
 *  whole so a page that owns three slices but sends two is still checked on the
 *  two — comparing the blob would call that a conflict. */
function parseFingerprint(fingerprint: string): Map<string, string> {
  return new Map(
    fingerprint
      .split('|')
      .filter(Boolean)
      .map((part) => {
        const at = part.indexOf(':');
        return [part.slice(0, at), part.slice(at + 1)] as const;
      }),
  );
}

/** The fingerprint to hand back after a write: every slice the caller was
 *  holding a digest for, plus every slice it just wrote. Narrowing it to the
 *  patch alone would silently drop the guard on a slice the page still owns but
 *  didn't send this time. */
export function nextStagingFingerprint(
  staging: CustomProposalStaging,
  patch: Partial<CustomProposalStaging>,
  expected: string | undefined,
): string {
  const keys = new Set<string>(
    Object.keys(patch).filter((key) => key !== RESET_REVISION_KEY),
  );
  if (expected) {
    for (const key of parseFingerprint(expected).keys()) {
      keys.add(key);
    }
  }
  return stagingFingerprint(staging, [...keys] as StagingSliceKey[]);
}

/** Optimistic-concurrency guard for a staging write, per slice.
 *
 *  Each step page seeds its form from the matrix and remembers the fingerprint
 *  of the slices it owns. Because forms are NOT shared across pages, two tabs
 *  can seed from the same matrix and then race their saves — and Items + Price
 *  both live in `bidGroups`, so one tab's whole-array write would silently
 *  clobber the other's edits. This rejects a write whose owned slices have moved
 *  since the caller read them; a write that lands beside an untouched slice is
 *  not a conflict and is accepted.
 *
 *  `expected` undefined = opt-out (the normalization/reset callers that rewrite
 *  the whole matrix keep last-write-wins). The check only bites when a caller
 *  opts in by passing the fingerprint it read, and only on the slices that
 *  fingerprint actually covers. */
export function assertStagingSlices(
  stored: CustomProposalStaging | null | undefined,
  patch: Partial<CustomProposalStaging>,
  expected: string | undefined,
): void {
  if (expected === undefined) {
    return;
  }
  const claimed = parseFingerprint(expected);
  const patchKeys = Object.keys(patch).filter(
    (key) => key !== RESET_REVISION_KEY,
  ) as StagingSliceKey[];
  const conflicted = patchKeys.some((key) => {
    const digest = claimed.get(key);
    return (
      digest !== undefined &&
      stagingFingerprint(stored, [key]) !== `${key}:${digest}`
    );
  });
  if (conflicted) {
    throw new ConflictError(
      'This bid was changed elsewhere since you opened it. Reload to get the latest, then re-apply your edit.',
    );
  }
}

/** Merge a PARTIAL staging patch onto the stored matrix — the contextual-merge
 *  the wizard's per-tab saves write against (was a full replace).
 *
 *  Rule: a key absent from `patch` is left untouched. `metadata` deep-merges
 *  (spread onto the stored metadata). Every other top-level key
 *  (`vendors` / `bidders` / `bidGroups` / `estimates`) REPLACES the stored value
 *  whole when present — so a page that owns an array sends its whole array, and
 *  a delete is simply that array minus the element.
 *  This keeps concerns independent: Details never sends the matrix, and a
 *  matrix page's write can't clobber a key it didn't send. */
export function mergeStaging(
  stored: CustomProposalStaging | null | undefined,
  patch: Partial<CustomProposalStaging>,
): CustomProposalStaging {
  const base: CustomProposalStaging = stored
    ? structuredClone(stored)
    : { vendors: [], bidders: [], bidGroups: [] };
  const merged: CustomProposalStaging = { ...base };
  if (patch.vendors !== undefined) {
    merged.vendors = patch.vendors;
  }
  if (patch.bidders !== undefined) {
    merged.bidders = patch.bidders;
  }
  if (patch.bidGroups !== undefined) {
    merged.bidGroups = patch.bidGroups;
  }
  if (patch.estimates !== undefined) {
    merged.estimates = patch.estimates;
  }
  if (patch.metadata !== undefined) {
    merged.metadata = { ...(base.metadata ?? {}), ...(patch.metadata ?? {}) };
  }
  return merged;
}

/** The items of a group, from its schedules (tree shape) or the flat fallback —
 *  the single idiom persist and enrichment both walk. */
export function stagingItemsOf(group: StagingBidGroup): StagingItem[] {
  return group.schedules && group.schedules.length > 0
    ? group.schedules.flatMap((s) => s.items ?? [])
    : (group.items ?? []);
}

/** Every line across every group, in stable document order. */
export function walkStagingItems(
  staging: CustomProposalStaging,
): StagingItem[] {
  return (staging.bidGroups ?? []).flatMap(stagingItemsOf);
}

/** The resolution decided for one line, positioned by its walk index. */
export interface LineEnrichment {
  itemMatch: 'existing' | 'new';
  itemId: string | null;
  categoryId: string | null;
  categoryCode: string | null;
  collectionCodes: string[];
  /** Collection memberships WITH their resolved factors (superset of
   *  {@link collectionCodes}). */
  collections?: LineCollection[];
  unitId: string | null;
  isTrueLumpSum: boolean;
  liquidAcPattern: string | null;
}

/** Merge the hunt's LINK fields onto staging, positionally (walk order matches
 *  {@link walkStagingItems}). READ fields (description/qty/unit/bids) are never
 *  touched. Pure — returns a new staging, mutates nothing. */
export function mergeEnrichmentIntoStaging(
  staging: CustomProposalStaging,
  enrichment: LineEnrichment[],
): CustomProposalStaging {
  let i = 0;
  const apply = (item: StagingItem): StagingItem => {
    const e = enrichment[i++];
    if (!e) {
      return item;
    }
    return {
      ...item,
      itemMatch: e.itemMatch,
      itemId: e.itemId,
      categoryId: e.categoryId,
      categoryCode: e.categoryCode,
      collectionCodes: e.collectionCodes,
      collections: e.collections,
      collectionId: e.collectionCodes[0] ?? null,
      unitId: e.unitId,
      isTrueLumpSum: e.isTrueLumpSum,
      liquidAcPattern: e.liquidAcPattern,
    };
  };
  return {
    ...staging,
    bidGroups: (staging.bidGroups ?? []).map((group) => ({
      ...group,
      schedules: group.schedules?.map((s) => ({
        ...s,
        items: (s.items ?? []).map(apply),
      })),
      items:
        group.schedules && group.schedules.length > 0
          ? group.items
          : group.items?.map(apply),
    })),
  };
}

function toStagingItem(item: ConsolidatedMatrixItem): StagingItem {
  return {
    itemLineId: item.itemLineId,
    // The line number the ENGINE read off the page. Dropping it here left every
    // line with a null number, so the wizard's Line column showed its position
    // placeholder for the whole document and publish had nothing to carry
    // (EDG-1896). A schedule that restarts at 1 is the printed truth, not a
    // duplicate to be flattened.
    lineNumber: item.lineNumber ?? null,
    itemNumber: item.itemNumber ?? null,
    description: item.description ?? null,
    quantity: item.quantity ?? null,
    unit: item.unit ?? null,
    itemId: null,
    categoryId: null,
    collectionId: null,
    bids: item.bids ?? undefined,
  };
}

/**
 * Mint the identities legacy staging never had (EDG-1629): its vendors carry no
 * `vendorColumnId` and its lines no `itemLineId`. A column keyed on a falsy id
 * makes the price sheet's table throw while building itself, and every write
 * locates a line by `itemLineId`, so an id-less line silently discards each
 * edit. Publish reads the same axes, so it must resolve them identically.
 *
 * The vendor NAME is the column fallback because legacy staging keys
 * `item.bids`, `totals` and `estimates[]` by the printed name — falling back to
 * it reconnects those cells instead of blanking the column. {@link
 * StagingVendor} declares a column id is NEVER derived from the name, and that
 * invariant is intact: it protects a rename from orphaning cells, and a rename
 * still only patches `name` while the id minted here stays frozen. This is the
 * one exception the rule allows for, a column that never had an id to keep. A
 * name already spoken for takes the next free `col-N` slot, since a shared id
 * would fuse two bidders into one column. Uniqueness is a promise about what
 * this MINTS: a duplicate already stored is left alone, because a stored id is
 * what `item.bids` and friends are filed under and re-keying one here would
 * hand that column an id with no cells behind it.
 *
 * Deterministic and idempotent, so every reader of a proposal derives the same
 * identity. That is what lets the sheet, its validation flags and publish agree
 * without the stored row ever being migrated.
 */
export function withStagingIds(
  staging: CustomProposalStaging | null | undefined,
): CustomProposalStaging | undefined {
  if (!staging) {
    return undefined;
  }
  // Ids already stored are claimed up front, so a vendor that owns the cells
  // filed under one can never lose them to an earlier vendor merely NAMED the
  // same thing. Everything minted below is checked against the same set.
  const stored = staging.vendors ?? [];
  const taken = new Set(
    stored.map((vendor) => vendor.vendorColumnId).filter(Boolean),
  );
  let spare = 0;
  const mint = (): string => {
    while (taken.has(`col-${spare}`)) {
      spare += 1;
    }
    return `col-${spare}`;
  };
  const vendors = stored.map((vendor) => {
    if (vendor.vendorColumnId) {
      return vendor;
    }
    const vendorColumnId =
      vendor.name && !taken.has(vendor.name) ? vendor.name : mint();
    taken.add(vendorColumnId);
    return { ...vendor, vendorColumnId };
  });
  let line = 0;
  const stamp = (item: StagingItem): StagingItem =>
    item.itemLineId ? item : { ...item, itemLineId: `line-${line++}` };
  const bidGroups = (staging.bidGroups ?? []).map((group) =>
    group.schedules?.length
      ? {
          ...group,
          schedules: group.schedules.map((schedule) => ({
            ...schedule,
            items: (schedule.items ?? []).map(stamp),
          })),
        }
      : { ...group, items: (group.items ?? []).map(stamp) },
  );
  return { ...staging, vendors, bidGroups };
}

/**
 * Normalize the CLASSIFY header's dates on the way through (EDG-1931). A document
 * extracted before the normalizer understood a fully-written date ("10:00 a.m.
 * Wednesday, October 22, 2025", "June 3, 2026 at 2:00 PM") stored `lettingDate`
 * null with only `lettingDatePrinted` filled, so the Details header showed blank
 * — the printed text was captured, just never converted. Converting it here
 * repairs new staging and the read of already-extracted documents without a
 * migration or re-extraction; their next staging save persists the repaired day.
 *
 * Lossless and idempotent, like {@link withStagingIds}: the printed text is moved
 * into `lettingDatePrinted` rather than overwritten, an already-ISO day converts
 * to itself, and text naming no unambiguous day stays null instead of being
 * guessed at.
 */
function withNormalizedDates(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!metadata) {
    return null;
  }
  // What the page PRINTED: the explicit capture when there is one, else a
  // `lettingDate` still holding the prose (extractions older than the printed
  // twin stored it there). An already-canonical day printed nothing.
  const stored =
    typeof metadata.lettingDate === 'string' ? metadata.lettingDate.trim() : '';
  const printed =
    metadata.lettingDatePrinted === null
      ? null
      : (metadata.lettingDatePrinted ??
        (stored && isoLettingDayOf(stored) !== stored ? stored : null));
  return {
    ...metadata,
    lettingDate:
      isoLettingDayOf(metadata.lettingDate) ?? isoLettingDayOf(printed),
    ...(printed ? { lettingDatePrinted: printed } : {}),
    // The completion date has no printed twin to fall back on, so a value that
    // won't normalize is left exactly as stored rather than dropped.
    completionDate:
      isoDayOf(metadata.completionDate) ?? metadata.completionDate ?? null,
  };
}

/** Repair dates on a staging matrix that was persisted before EDG-1931. */
export function withNormalizedStagingDates(
  staging: CustomProposalStaging | null | undefined,
): CustomProposalStaging | null | undefined {
  if (!staging?.metadata) {
    return staging;
  }
  const metadata = staging.metadata;
  if (
    !('lettingDate' in metadata) &&
    !('lettingDatePrinted' in metadata) &&
    !('completionDate' in metadata)
  ) {
    return staging;
  }
  return {
    ...staging,
    metadata: withNormalizedDates(metadata),
  } as CustomProposalStaging;
}

/**
 * Wrap a raw consolidated matrix into the custom proposal's staging shape. The READ
 * values are copied verbatim (group/schedule names preserved); every LINK field
 * starts `null`. Pure — no resolution happens here.
 */
export function consolidatedToStaging(
  matrix: ConsolidatedMatrix,
): CustomProposalStaging {
  // The engine already minted each column's `vendorColumnId` at assemble; carry
  // it verbatim (never re-mint) so the column identity is stable end-to-end. The
  // name is the label; a blank-named column is dropped.
  const columns = matrixBidders(matrix);
  // Through the repair on the way out: a legacy matrix has no ids to carry, and
  // every path that seeds from here WRITES the result (reset-to-extraction,
  // pickPrimaryExtraction, the normalize seed). Emitting an id-less shape would
  // undo the repair a reader already saved. Idempotent, so a modern matrix keeps
  // the ids `assemble` minted.
  return withStagingIds({
    // `bidders` (the bare name list) is kept only for readers that still want
    // names; the column axis of record is `vendors[]`, keyed by vendorColumnId.
    bidders: columns.map((b) => b.name),
    vendors: columns.map((b) => ({
      vendorColumnId: b.vendorColumnId,
      name: b.name,
      globalVendorId: null,
      vendorId: null,
      // Carry the invalid-bid status + reason through so the sheet can exclude it
      // from ranking and badge it. `MatrixBidder.status` is invalid-only (a valid
      // bid leaves it absent), so its presence IS the signal.
      ...(b.status
        ? { status: b.status, statusReason: b.statusReason ?? null }
        : {}),
    })),
    bidGroups: (matrix.bidGroups ?? []).map((group) => ({
      type: group.type ?? null,
      name: group.name ?? null,
      // The award toggle is a reviewer decision that lives only in staging; a
      // fresh extraction carries none. Preserved here for the round-trip so a
      // re-seed from a matrix that already has it (a published proposal) keeps it.
      awarded: group.awarded ?? undefined,
      schedules: group.schedules?.map((schedule) => ({
        name: schedule.name ?? null,
        items: (schedule.items ?? []).map(toStagingItem),
        // The printed total is the reconcile oracle — carry it verbatim so the
        // price sheet can grade each subtotal against it. Dropping it here left
        // freshly-seeded proposals with no total to reconcile against.
        total: schedule.total ?? null,
      })),
      items: group.items?.map(toStagingItem),
      totals: group.totals ?? null,
    })),
    // Carry the estimate flag through — publish reads `estimates[]` (columnIds)
    // to divert the engineer's estimate to cost-estimate. Dropping it shipped the
    // estimate as a winning vendor.
    estimates: matrix.estimates ?? undefined,
    // Carry the CLASSIFY header (letting date, contract/project numbers, cost
    // estimate, …) through so the Details form shows it — only the dates are
    // touched, and only to normalize them. Normalize later stamps the resolved
    // ownerId/stateId on top of this.
    metadata: withNormalizedDates(matrix.metadata),
  }) as CustomProposalStaging;
}
